<template>
  <div v-if="isActive" class="modal__component" v-bind="$attrs">
    <div id="backdrop" class="fade modal-backdrop" :class="{ 'blur': !!blur, 'in': isActive, 'light': !!light }"
      @click="closeModal"></div>
    <div v-if="blur" class="blur-effect"></div>
    <div
      :id="id"
      :class="[modalClasses, {
         'borderless': !!borderless,
         'collapsetomodal': isCollapsetoModal && isLessThanLargeDesktop ,
         'full-width': !!fullWidth,
         'modal-detail-service': isModalDetailService,
         'slide-fade-down-enter-active': !!animate,
         'staticmodal': isCollapsetoModal && isLessThanLargeDesktop ,
         'vuemodale': !isCollapsetoModal || !isLessThanLargeDesktop ,
      }]"
      data-once="modal"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="getId"
    >
      <div ref="firstFocusModal" class="firstfocusmodal" tabindex="0"></div>
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header" v-if="!noHeader">
            <slot name="header" :aria-labelledby="getId">
              <button v-if="isClosable" type="button" @click="closeModal" class="btn-close" aria-label="Fermer">
                <i class="icon-close" aria-hidden="true" />
                <span class="sr-only">Fermer</span>
              </button>
              <button v-else type="button" class="btn-close center-content"
                @click="hasBackFunction ? $emit('on-go-back') : closeModal">
                <i class="icon-arrow" aria-hidden="true"></i>
                <span class="sr-only">Fermer</span>
                {{ title }}
              </button>
              <div v-if="subtitle" class="wrapper-title">
                <h2 :id="getId" class="title">{{ subtitle }}</h2>
              </div>
            </slot>
          </div>

          <div class="modal-body" :style="noHeader ? 'padding:0px;' : ''">
            <slot name="body">
              <slot />
            </slot>
          </div>
        </div>
      </div>
      <div ref="lastFocusModal" class="lastfocusmodal" tabindex="0"></div>
    </div>
  </div>
</template>

<script>
import {
  computed, inject, nextTick, ref, watch, onBeforeUnmount,
} from 'vue';
import { hashGenerator } from '@/helpers';

export default {
  inheritAttrs: false,
  props: {
    // add a slide down to top animation
    animate: {
      default: () => false,
      type: Boolean,
    },
    // add a blur filter above backdrop
    blur: {
      default: () => false,
      type: Boolean,
    },
    // slots content will be stick to modal-[header-body]
    borderless: {
      default: () => false,
      type: Boolean,
    },
    // modal will be full width with document
    fullWidth: {
      default: () => false,
      type: Boolean,
    },
    id: {
      default: () => 'modalDetailService',
      type: String,
    },
    // white backdrop
    light: {
      default: () => false,
      type: Boolean,
    },
    modalClasses: {
      default: () => '',
      type: String,
    },
    noHeader: {
      default: () => false,
      type: Boolean,
    },
    title: {
      type: String,
      default: '',
    },
    subtitle: {
      type: String,
      default: '',
    },
    ariaLabelledby: {
      type: String,
      default: 'titlemodale',
    },
    isCollapsetoModal: {
      type: Boolean,
      default: false,
    },
    isClosable: {
      type: Boolean,
      default: true,
    },
    uniquePrefix: {
      type: Boolean,
      default: true,
    },
    hasBackFunction: {
      type: Boolean,
      default: false,
    },
    isModalDetailService: {
      type: Boolean,
      default: false,
    },
    isActive: {
      type: Boolean,
      default: true,
    },
  },

  emits: [
    'on-close-modal',
    'on-go-back',
  ],

  setup(props, { emit }) {
    const firstFocusModal = ref(null);
    const isLessThanLargeDesktop = inject('isLessThanLargeDesktop');
    const lastFocusModal = ref(null);

    const getId = computed(() => {
      const hash = props.uniquePrefix ? `-${hashGenerator()}` : '';
      return `${props.ariaLabelledby}${hash}`;
    });

    const keyHandler = (e) => {
      if (e.code === 'Escape') {
        emit('on-close-modal');
      }

      if (e.code === 'Tab') {
        if (document.activeElement === lastFocusModal.value) {
          firstFocusModal.value.focus();
        }
      }

      if (e.shiftKey && e.code === 'Tab') {
        if (document.activeElement === firstFocusModal.value) {
          lastFocusModal.value.focus();
        }
      }
    };

    watch(() => props.isActive, () => {
      if (props.isActive) {
        nextTick(() => {
          firstFocusModal.value.focus();
          document.body.addEventListener('keydown', keyHandler);
        });
      } else {
        document.body.removeEventListener('keydown', keyHandler);
      }
    }, { immediate: true });

    onBeforeUnmount(() => {
      document.body.removeEventListener('keydown', keyHandler);
    });

    const closeModal = () => {
      emit('on-close-modal');
    };

    return {
      closeModal,
      firstFocusModal,
      getId,
      isLessThanLargeDesktop,
      lastFocusModal,
    };
  },
};
</script>
